import { useForm } from "react-hook-form";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Share } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import moment from "moment";
import debounce from "../../utilis/methods/networkHandling";
import CustomTableBody from "../../utilis/component/CustomTableBody";
import { FormOnChangeEventType } from "../../EMR/types";
import { CustomFormField } from "../../utilis/component/CustomFormElement";
import { LabListResponseType, LabSearchPayoad } from "./type";
import { useNavigate } from "react-router-dom";
import { base } from "../../constants";
import dashboardRouteList from "../routeList";
import { getLabSearch, sendLabRadiologyReport } from "./services";
import moduleRoutes from "../../routes/moduleRouteList";
import usePopup from "../../hooks/usePopup";
// import useAuthentication from "@hooks/useAuthentication";

const WhatsAppIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="28"
    height="28"
    fill="#25D366"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const LabLandingPage = () => {
  const { control } = useForm();
  const navigate = useNavigate();
  const { showSnackbar } = usePopup();
  const [sendLoadingIndex, setSendLoadingIndex] = useState(-1);
  const [sharePopup, setSharePopup] = useState<{
    open: boolean;
    labId: number | null;
    encounterId: number | null;
    index: number;
  }>({ open: false, labId: null, encounterId: null, index: -1 });
  const [searchPayload, setSearchPayload] = useState<LabSearchPayoad>({
    searchText: "",
    searchDate: moment().format("YYYY-MM-DD"),
    pageNumber: 0,
    pageSize: 25,
    sortField: "",
    sortOrder: "asc",
  });

  const { data: LabList, isLoading } = useQuery(
    ["getLabSearch", searchPayload],
    async () => await getLabSearch(searchPayload),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: sendReport, isLoading: sendReportLoading } = useMutation(
    sendLabRadiologyReport,
    {
      onSuccess: (result) => {
        if (result.responseIndicator === "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 3000,
          });
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message:
            e?.response?.data?.responseMessage || "Something went wrong !",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
      onSettled: () => {
        setSendLoadingIndex(-1);
        setSharePopup({
          open: false,
          labId: null,
          encounterId: null,
          index: -1,
        });
      },
    },
  );

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const pageSize = parseInt(event.target.value, 10);
    setSearchPayload((prev) => ({
      ...prev,
      pageNumber: 0,
      pageSize,
    }));
  };

  const handleChangePage = (_: unknown, pageNumber: number) => {
    setSearchPayload({
      ...searchPayload,
      pageNumber,
    });
  };

  const searchOnText = debounce((e: FormOnChangeEventType) => {
    setSearchPayload((prev) => ({
      ...prev,
      searchText: e.target.value,
    }));
  }, 500);

  const searchByDate = (e: FormOnChangeEventType) => {
    setSearchPayload((prev) => ({
      ...prev,
      searchDate: moment(e.target.value).format("YYYY-MM-DD"),
    }));
  };

  const handleOpenShare = (
    row: LabListResponseType,
    index: number,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setSharePopup({
      open: true,
      labId: row.labId,
      encounterId: row.encounterID,
      index,
    });
  };

  const handleCloseShare = () => {
    if (!sendReportLoading) {
      setSharePopup({
        open: false,
        labId: null,
        encounterId: null,
        index: -1,
      });
    }
  };

  const handleSendReport = (labId: number, encounterId: number, index: number) => {
    setSendLoadingIndex(index);
    sendReport({
      reportType: "LAB",
      encounterId,
      reqId: labId,
    });
  };

  const labPatientList = useMemo(()=>{
    if(LabList?.response?.items?.length){
        return LabList?.response?.items
    }else{
     return []
    }
 },[LabList?.response?.items])
 
//  const{user}= useAuthentication();
//  const regionValue= user?.region;
//    const idHeading =
//  regionValue === "QATAR_REGION"
//    ? "Qatar ID"
//    : "Emirates Id";
const idHeading = "Civil ID"
 const headings = [
  "Sl No",
  "EMR No/Reg No",
  "Patient Name",
  "Mobile No",
  idHeading,
  "Status",
  "Actions",
];
  return (
    <div className="px-3 pt-4">
      <Paper className="flex flex-col gap-2">
        <TableContainer className=" relative ">
          <div className="px-4 py-2 border-b">
            <Typography variant="h6">Lab Patients</Typography>
          </div>
          <form className="py-4 px-2 grid grid-cols-4 gap-2">
            <CustomFormField
              control={control}
              element="datepicker"
              fieldProps={{ label: "Search date" }}
              name="searchDate"
              rules={{ required: false, onChange: searchByDate }}
            />
            <CustomFormField
              control={control}
              element="input"
              fieldProps={{ label: "Search name" }}
              name="searchText"
              rules={{ required: false, onChange: searchOnText }}
            />
          </form>
          <Table size="small" aria-label="simple table" className="border-b">
            <TableHead
              sx={(theme) => ({
                backgroundColor: theme.palette.primary.main,
              })}
              className="sticky w-full top-0 bg-inherit z-50"
            >
              <TableRow>
                {headings.map((title, index) => (
                  <TableCell
                    key={`radiolist${index}`}
                    sx={(theme) => ({
                      color: theme.palette.background.paper,
                      fontWeight: 600,
                      fontSize: 14,
                    })}
                  >
                    {title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <CustomTableBody
              alert={"No records"}
              dataLoading={isLoading}
              isData={Boolean(LabList?.response?.items?.length)}
              totalSpan={{ col: headings.length, row: 10 }}
            >
              {labPatientList.map((row: LabListResponseType, index: number) => (
                <TableRow
                  onClick={() => {
                    navigate(
                      `${base}${moduleRoutes.mainRoute}/${moduleRoutes.dashboard}/${dashboardRouteList.lab}/${row.encounterID}?emrno=${row?.emrNo || ""}`
                    );
                  }}
                  className="hover:bg-gray-100"
                  key={`emrlistId${index}`}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    cursor: "pointer",
                  }}
                >
                  <TableCell>{searchPayload.pageNumber * searchPayload.pageSize + (index + 1)}</TableCell>
                  <TableCell>{row.emrNo}</TableCell>
                  <TableCell>{row.firstName}</TableCell>
                  <TableCell>{row.MobileNo}</TableCell>
                  <TableCell>{row.emiratesId}</TableCell>
                  <TableCell>
                    {row.isPerformed ? (
                      row.isCashed ? (
                        <span className="text-green-600 font-bold">
                          Completed
                        </span>
                      ) : (
                        <span className="text-yellow-600 font-bold">
                          Performed —{" "}
                          <span className="text-red-400 font-bold">
                            ( Not Paid)
                          </span>
                        </span>
                      )
                    ) : (
                      <span className="text-red-400 font-bold">
                        Not Performed 
                        <span
                          className={
                            row.isCashed
                              ? "text-green-400 font-bold"
                              : "text-red-400 font-bold"
                          }
                        >
                          ({row.isCashed ? "Paid" : "Not Paid"})
                        </span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.isPerformed && (
                      <Tooltip title="Share lab report">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => handleOpenShare(row, index, e)}
                        >
                          <Share fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </CustomTableBody>
          </Table>
        </TableContainer>
        <div>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={LabList?.response?.totalCount || 0}
            rowsPerPage={searchPayload.pageSize}
            page={searchPayload.pageNumber}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </Paper>
      <Dialog
        open={sharePopup.open}
        onClose={handleCloseShare}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Share Report</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
            }}
          >
            <Tooltip title="Share via WhatsApp">
              <IconButton
                disabled={sendReportLoading}
                onClick={() => {
                  if (
                    sharePopup.labId !== null &&
                    sharePopup.encounterId !== null
                  ) {
                    handleSendReport(
                      sharePopup.labId,
                      sharePopup.encounterId,
                      sharePopup.index,
                    );
                  }
                }}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1.5,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                {sharePopup.index === sendLoadingIndex && sendReportLoading ? (
                  <CircularProgress size={28} color="success" />
                ) : (
                  <WhatsAppIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default LabLandingPage;


